/**
 * DroidDatapad — THE DROID TERMINAL (2026-09-24): one window for every droid you own (the SWG datapad).
 *
 *   left   your deployed droids (class, mode, health, pool) and the droid items in your bag (Deploy)
 *   right  the selected droid, seven tabs:
 *          Orders     run / stop / restart / come / follow / home / scan / recall + the class card
 *          Program    DroidScript 2: prebuilt programs, an editor with line numbers that checks as you
 *                     type (errors name the line; a live cost line), upload, and the API reference
 *          Cargo      what it carries, prices, unloading; loading from your bag
 *          Upkeep     the maintenance pool: fund, withdraw, repair, how long the pool lasts
 *          Estimator  THE COST CALCULATOR: any program on any class — per hour, per pass, a pool's
 *                     runtime, the cost of N hours, a per-line breakdown, every class compared
 *          Reports    surveys (a heat map + the best spots), notes, ship and storage reports
 *          Log        the droid's log
 *
 * Hands-on work (upload, cargo, repair) needs you at the droid (10 m); orders, upkeep, reports and the
 * estimator work anywhere. THE TOKEN LAW: colours are `--ax-*` tokens only.
 */
import { makeWindow, type UiWindow } from '../../lib/ui_window.js';
import { atlas } from '../../state.js';
import { bus } from '../../bus.js';
import { sfx } from '../../lib/sfx.js';
import {
  DROID_CLASSES, DROID_CLASS_ORDER, droidStats, MILLI, runtimeHours, costForHours, refundableCredits, droidReachM, UPKEEP_CALL_MAX, UPKEEP_HOUR_CAP, type DroidClass,
} from '../../lib/droid_classes.js';
import { estimateDroid, apiReference, type Estimate } from '../../lib/droid_lang.js';
import { templatesFor } from '../../lib/droid_templates.js';
import { classOfKind, credits, duration, surveyHeat, moneyOf, livePosOf, type SurveyPoint, type DroidMoney } from '../../lib/droid_view.js';
import { SCHEMATIC_BY_KEY } from '../../lib/schematic_catalog.js';
import { themeColor, onThemeChange } from '../../lib/ui_theme.js';
import { mixCanvasColor } from '../../lib/css_color.js';

type Tab = 'orders' | 'program' | 'cargo' | 'upkeep' | 'estimator' | 'reports' | 'log';
const TABS: Array<[Tab, string]> = [
  ['orders', 'Orders'], ['program', 'Program'], ['cargo', 'Cargo'], ['upkeep', 'Upkeep'],
  ['estimator', 'Estimator'], ['reports', 'Reports'], ['log', 'Log'],
];
const NEAR_M = 10;

const CSS = `
#droid-datapad .dd-root{display:flex;height:100%;min-height:0;font:13px/1.45 var(--ax-font);color:var(--ax-text)}
#droid-datapad .dd-rail{width:190px;flex:0 0 auto;border-right:1px solid var(--ax-line);overflow-y:auto;padding:8px;display:flex;flex-direction:column;gap:6px;background:var(--ax-bg2)}
#droid-datapad .dd-main{flex:1 1 auto;min-width:0;display:flex;flex-direction:column}
#droid-datapad .dd-card{border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:6px 8px;cursor:pointer;background:var(--ax-bg)}
#droid-datapad .dd-card.on{border-color:var(--ax-accent);box-shadow:inset 3px 0 0 var(--ax-accent)}
#droid-datapad .dd-card .t{font-weight:600;color:var(--ax-hi)}
#droid-datapad .dd-card .s{color:var(--ax-muted);font-size:12px}
#droid-datapad .dd-bar{height:5px;border-radius:var(--ax-radius);background:var(--ax-bg3);overflow:hidden;margin-top:4px}
#droid-datapad .dd-bar i{display:block;height:100%;background:var(--ax-good)}
#droid-datapad .dd-bar.hurt i{background:var(--ax-warn)}
#droid-datapad .dd-bar.low i{background:var(--ax-bad)}
#droid-datapad .dd-head{padding:8px 12px 6px;border-bottom:1px solid var(--ax-line)}
#droid-datapad .dd-head .n{font:600 15px/1.3 var(--ax-display);color:var(--ax-hi)}
#droid-datapad .dd-head .m{color:var(--ax-muted);font-size:12px}
#droid-datapad .dd-tabs{padding:6px 12px;border-bottom:1px solid var(--ax-line);display:flex;flex-wrap:wrap;gap:6px}
#droid-datapad .dd-body{flex:1 1 auto;overflow-y:auto;padding:10px 12px;min-height:0}
#droid-datapad .dd-row{display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin:6px 0}
#droid-datapad .dd-kv{display:grid;grid-template-columns:auto 1fr;gap:2px 12px;margin:6px 0}
#droid-datapad .dd-kv .k{color:var(--ax-muted)}
#droid-datapad .dd-big{font:600 20px/1.2 var(--ax-mono);color:var(--ax-hi)}
#droid-datapad .dd-note{color:var(--ax-muted);font-size:12px}
#droid-datapad .dd-reach{color:var(--ax-warn);font-size:13px;margin:6px 0 0}
#droid-datapad .dd-err{color:var(--ax-bad)}
#droid-datapad .dd-warn{color:var(--ax-warn)}
#droid-datapad .dd-okc{color:var(--ax-good)}
#droid-datapad .dd-ed{display:flex;border:1px solid var(--ax-line2);border-radius:var(--ax-radius);background:var(--ax-bg2);height:260px;overflow:hidden}
#droid-datapad .dd-gut{width:34px;flex:0 0 auto;text-align:right;padding:6px 4px;color:var(--ax-muted);font:12px/1.5 var(--ax-mono);overflow:hidden;user-select:none;border-right:1px solid var(--ax-line);white-space:pre}
#droid-datapad .dd-gut b{color:var(--ax-bad)}
#droid-datapad .dd-gut u{color:var(--ax-accent);text-decoration:none}
#droid-datapad textarea.dd-src{flex:1 1 auto;border:0;outline:0;resize:none;background:transparent;color:var(--ax-text);font:12px/1.5 var(--ax-mono);padding:6px 8px;white-space:pre;overflow:auto;tab-size:2}
#droid-datapad table{border-collapse:collapse;width:100%;font-size:12px}
#droid-datapad th{text-align:left;color:var(--ax-muted);font-weight:500;border-bottom:1px solid var(--ax-line);padding:3px 6px}
#droid-datapad td{border-bottom:1px solid color-mix(in srgb, var(--ax-line) 60%, transparent);padding:3px 6px;vertical-align:top}
#droid-datapad td.num{text-align:right;font-family:var(--ax-mono)}
#droid-datapad .dd-api{max-height:220px;overflow-y:auto;border:1px solid var(--ax-line);border-radius:var(--ax-radius);margin-top:6px}
#droid-datapad .dd-api tr.off td{opacity:.45}
#droid-datapad .dd-api tr:hover td{background:var(--ax-bg3);cursor:pointer}
#droid-datapad .dd-api code{font:12px var(--ax-mono);color:var(--ax-hi)}
#droid-datapad .dd-log{font:12px/1.5 var(--ax-mono);white-space:pre-wrap;color:var(--ax-soft)}
#droid-datapad input.ax-input{width:88px}
#droid-datapad .dd-grow{flex:1 1 auto}
#droid-datapad .dd-map{border:1px solid var(--ax-line);border-radius:var(--ax-radius);image-rendering:pixelated;width:100%;max-width:300px}
`;

interface DroidRowLike {
  droidId: bigint; owner: { isEqual(o: unknown): boolean }; worldId: bigint; kind: string; name: string; quality: number;
  posX: number; posZ: number; yaw: number; homeX: number; homeZ: number; mode: string; programSrc: string; logJson: string;
}

/** What a rebuild must not lose (2026-09-25): scroll offsets (`data-scroll`), the values the player typed (`data-k` +
 *  `data-edited`), and the control that held the focus (with its caret). */
interface Snap { scroll: Array<[string, number, number]>; values: Map<string, string>; focus: string | null; caret: [number, number] | null }

/** A whole number from a typed field, clamped to [min, max] (an overflow like `1e400` clamps to the bound, so the
 *  server names the real range), or `dflt` when it is not a number — `BigInt(Infinity)` threw inside the click
 *  handler and the player heard nothing (2026-09-25). */
function whole(v: string, dflt: number, min: number, max: number): number {
  const n = Math.floor(Number(v));
  return Math.min(max, Math.max(min, Number.isNaN(n) ? dflt : n));
}


/** Blend two theme colours for the survey heat map IN JS (2026-09-25): a canvas `fillStyle` handed `color-mix()` is
 *  silently ignored where the canvas parser lacks it, and every cell then keeps the previous fill. Any token syntax is
 *  resolved to plain rgb (lib/css_color — never a color-mix() to the canvas, gaps wave round 2). */
function mixColor(a: string, b: string, t: number): string {
  return mixCanvasColor(a, b, t);
}

export class DroidDatapad {
  private win: UiWindow | null = null;
  private sel: bigint | null = null;
  private tab: Tab = 'orders';
  private timer: number | null = null;
  private srcDraft = new Map<string, string>();
  private estOpts = { cls: '' as DroidClass | '', quality: 0, hours: 8, pool: -1, loopGuess: 3, eventsPerHour: 12 };
  private apiFilter = '';
  private reportSel: bigint | null = null;
  /** SOFT RENDER signatures (2026-09-25): the rail + header, and the open tab's content, each redrawn only when ITS
   *  data changed. One signature held every droid's pool (it drains every second) and the distance, so the whole tab
   *  was rebuilt under the player's cursor each tick: the Estimator's text box and fields, Upkeep's Fund / Withdraw and
   *  Cargo's price and quantity boxes lost their focus, their caret and what was typed. */
  private chromeSig = '';
  private bodySig = '';
  /** Which droid + tab the built window shows (a snapshot is restored only into the same one). */
  private ctx = '';
  /** The open tab's in-place refresh (gates, the pool's numbers) — set by the tab's builder, run on every tick. */
  private live: (() => void) | null = null;
  /** The Upkeep tab's hourly rate, memoised by source + class + quality + home (it compiled the program every tick). */
  private rateMemo = { key: '', perHour: 0, ok: false };

  isOpen(): boolean { return !!this.win?.isOpen(); }

  open(droidId?: bigint | null, tab?: Tab): void {
    this.ensure();
    if (droidId !== undefined && droidId !== null) this.sel = droidId;
    if (tab) this.tab = tab;
    if (this.sel === null) this.sel = this.mine()[0]?.droidId ?? null;
    this.win!.setOpen(true);
    document.body.dataset.droidTerminal = '1'; // the Lua IDE may open while a droid is being programmed
    this.render(true);
  }

  close(): void {
    this.win?.setOpen(false);
    delete document.body.dataset.droidTerminal;
  }

  toggle(droidId?: bigint | null): void {
    if (this.isOpen() && (droidId === undefined || droidId === null || droidId === this.sel)) this.close();
    else this.open(droidId ?? undefined);
  }

  dispose(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.win?.root.remove();
    document.getElementById('droid-datapad-css')?.remove();
  }

  // ─── data ──────────────────────────────────────────────────────────────────

  private conn() { return atlas.conn; }

  mine(): DroidRowLike[] {
    const c = this.conn();
    if (!c?.identity) return [];
    const out: DroidRowLike[] = [];
    try { for (const d of c.db.droidUnit.droidUnitByOwner.filter(c.identity)) out.push(d as unknown as DroidRowLike); } catch { /* cache churn */ }
    return out.sort((a, b) => (a.droidId < b.droidId ? -1 : 1));
  }

  private droid(): DroidRowLike | null {
    const c = this.conn();
    if (!c || this.sel === null) return null;
    return (c.db.droidUnit.droidId.find(this.sel) as unknown as DroidRowLike) ?? null;
  }

  private vitals(id: bigint) { return this.conn()?.db.droidVitals.droidId.find(id) ?? null; }
  /** The droid's money — the owner's purse (droid_purse, row-secured), the legacy vitals columns before the migration. */
  private money(id: bigint): DroidMoney {
    let p = null;
    try { p = this.conn()?.db.droidPurse.droidId.find(id) ?? null; } catch { /* pre-publish */ }
    return moneyOf(p, this.vitals(id));
  }
  /** Where the droid IS (droid_pos — the unit row's position is a snapshot since 2026-09-28). */
  private pos(d: DroidRowLike): { x: number; z: number; yaw: number } {
    let p = null;
    try { p = this.conn()?.db.droidPos.droidId.find(d.droidId) ?? null; } catch { /* pre-publish */ }
    return livePosOf(p, d);
  }
  private brain(id: bigint) { return this.conn()?.db.droidBrain.droidId.find(id) ?? null; }

  /** The droid's program source: its owner's `droid_source` row first (2026-09-25 — the source left the public
   *  droid_unit row, which now carries it EMPTY for every new upload), else the pre-migration `programSrc`. The SDK's
   *  row cache, indexed by droidId, is the map the subscription keeps. */
  private source(d: DroidRowLike): string {
    let s: string | undefined;
    try { s = this.conn()?.db.droidSource.droidId.find(d.droidId)?.source; } catch { /* cache churn */ }
    return s ?? d.programSrc ?? '';
  }

  private cargo(id: bigint) {
    const c = this.conn();
    if (!c) return [];
    return [...c.db.droidCargo.droidCargoByDroid.filter(id)];
  }

  private distTo(d: DroidRowLike): number {
    if (atlas.currentWorldId !== d.worldId) return Infinity;
    const p = this.pos(d);
    return Math.hypot(p.x - atlas.localX, p.z - atlas.localZ);
  }

  /** The terminal's reach, measured from the droid's HULL (droidReachM — the server's own check). */
  private reach(d: DroidRowLike): number { return droidReachM(d.kind, NEAR_M); }
  private isNear(d: DroidRowLike): boolean { return this.distTo(d) <= this.reach(d); }

  private call(fn: () => Promise<unknown> | undefined, okMsg?: string): void {
    sfx('sfx_droid_beep', { gain: 0.35, cooldownMs: 150 });
    const fail = (e: unknown): void => { bus.emit('atlas:toast', { text: String((e as Error)?.message ?? e).replace(/^SenderError:\s*/, ''), kind: 'error' }); };
    let p: Promise<unknown> | undefined;
    try { p = fn(); } catch (e) { fail(e); return; } // an argument the SDK refuses throws HERE, before any promise
    if (!p) return;
    // A SOFT refresh after an action lands (2026-09-25): a forced rebuild replaced the whole tab — the editor too, had
    // the player clicked back into it while the reducer was in flight.
    p.then(() => { if (okMsg) bus.emit('atlas:toast', { text: okMsg, kind: 'success', ms: 2500 }); this.refresh(); })
      .catch(fail);
  }

  // ─── window ────────────────────────────────────────────────────────────────

  private ensure(): void {
    if (this.win) return;
    if (!document.getElementById('droid-datapad-css')) {
      const st = document.createElement('style');
      st.id = 'droid-datapad-css';
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    this.win = makeWindow({ id: 'droid-datapad', title: 'Droids', icon: 'droid', width: 760, height: 600, minWidth: 560, minHeight: 380, onClose: () => { delete document.body.dataset.droidTerminal; } });
    this.win.root.id = 'droid-datapad';
    // A field the player typed into is marked, so a rebuild puts back what they typed rather than the default.
    const mark = (e: Event): void => { const t = e.target as HTMLElement | null; if (t?.dataset?.k && t.dataset.edited !== '1') t.dataset.edited = '1'; };
    this.win.body.addEventListener('input', mark);
    this.win.body.addEventListener('change', mark);
    this.timer = window.setInterval(() => { if (this.isOpen()) this.render(false); }, 1000);
    // A theme change repaints the survey heat map (a canvas keeps the colours it was painted with — gaps wave round 2).
    onThemeChange(() => { if (this.isOpen()) this.refresh(); });
  }

  private el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
  }

  /** A button; `key` (default: its label) lets a rebuild hand the focus back to the same control. */
  private btn(label: string, onClick: () => void, primary = false, title = '', key = `b:${label}`): HTMLButtonElement {
    const b = this.el('button', primary ? 'ax-btn is-primary' : 'ax-btn', label);
    if (title) b.title = title;
    b.dataset.k = key;
    b.onclick = onClick;
    return b;
  }

  /** A number field; `key` lets a rebuild put back what the player typed (and the focus). */
  private num(value: number, min: number, max: number, step = 1, key = ''): HTMLInputElement {
    const i = this.el('input', 'ax-input ax-num');
    i.type = 'number'; i.min = String(min); i.max = String(max); i.step = String(step); i.value = String(value);
    if (key) i.dataset.k = key;
    return i;
  }

  /**
   * SOFT RENDER (2026-09-25). A full build only when forced (open, a tab or a droid picked) or when the droid / tab
   * changed. Otherwise: the rail and the header are patched in place when THEIR data changed, the open tab's live parts
   * (the upload gate, the pool's numbers, reach notes) refresh in place, and the tab's content is rebuilt only when its
   * own data changed — never while the player is typing in it (it waits for the blur), and the Program tab's editor
   * never. Scroll offsets, typed values and the focused control survive every rebuild (`snapshot` / `restore`).
   */
  private render(force: boolean): void {
    if (!this.win) return;
    let d = this.droid();
    // Nothing selected, or a recalled droid left the selection on a row that no longer exists: take the first one you
    // own (a droid deployed from the rail while the window said "No droid deployed" never appeared until a reopen).
    if (!d) { const first = this.mine()[0]; if (first) { this.sel = first.droidId; d = this.droid(); } }
    const ctx = `${this.sel ?? ''}|${d ? this.tab : 'none'}`;
    if (force || ctx !== this.ctx || !this.win.body.querySelector('.dd-root')) { this.build(d, ctx); return; }
    const chrome = this.chromeSignature(d);
    if (chrome !== this.chromeSig) { this.refreshChrome(d); this.chromeSig = chrome; }
    this.live?.();
    if (this.bodySignature(d) !== this.bodySig && this.rebuildContent(d)) this.bodySig = this.bodySignature(d);
  }

  /** After an action lands: soft-render with the content's signature invalidated. */
  private refresh(): void {
    this.bodySig = '';
    this.chromeSig = '';
    this.render(false);
  }

  private build(d: DroidRowLike | null, ctx: string): void {
    const win = this.win!;
    const body = win.body;
    // Another droid or tab: its own values and scroll — only the rail's scroll and the focused control (a card, a tab,
    // the button just clicked) carry over, so the pad's ring is not thrown back to the top on every pick.
    const snap = this.snapshot(body);
    if (ctx !== this.ctx) {
      snap.values.clear();
      snap.caret = null;
      snap.scroll = snap.scroll.filter(([k]) => k === 'rail');
      // Only the rail's and the tab strip's controls mean the same thing on another droid (never hand the focus to,
      // say, the next droid's Recall).
      if (snap.focus && !/^(card|deploy|tab):/.test(snap.focus)) snap.focus = null;
    }
    this.ctx = ctx;
    body.textContent = '';
    const root = this.el('div', 'dd-root');
    const rail = this.el('div', 'dd-rail');
    rail.dataset.role = 'rail';
    rail.dataset.scroll = 'rail';
    const main = this.el('div', 'dd-main');
    root.append(rail, main);
    body.appendChild(root);
    this.fillRail(rail);
    if (d) {
      win.setTitle(`Droids — ${d.name}`);
      const head = this.header(d);
      head.dataset.role = 'dd-header';
      main.appendChild(head);
      const tabs = this.el('div', 'dd-tabs');
      const seg = this.el('div', 'ax-seg');
      for (const [k, label] of TABS) {
        const b = this.el('button', k === this.tab ? 'on' : '', label);
        b.dataset.tab = k;
        b.dataset.k = `tab:${k}`;
        b.onclick = () => { this.tab = k; this.render(true); };
        seg.appendChild(b);
      }
      tabs.appendChild(seg);
      main.appendChild(tabs);
    } else win.setTitle('Droids');
    main.appendChild(this.content(d));
    this.chromeSig = this.chromeSignature(d);
    this.bodySig = this.bodySignature(d);
    this.restore(body, snap);
  }

  /** The open tab's content, built fresh (its builder sets `live`). */
  private content(d: DroidRowLike | null): HTMLDivElement {
    this.live = null;
    const content = this.el('div', 'dd-body');
    content.dataset.role = 'content';
    content.dataset.scroll = 'content';
    if (!d) {
      content.append(this.el('div', 'ax-head', 'No droid deployed'),
        this.el('p', 'dd-note', 'Deploy a droid from your bag (on the left) while you stand on the Commons. Craft one at the Workbench — the Supply Droid is basic work; the other classes need their schematic from a dealer.'));
      this.estimatorInto(content, null);
      return content;
    }
    content.dataset.tabBody = this.tab;
    switch (this.tab) {
      case 'orders': this.orders(content, d); break;
      case 'program': this.program(content, d); break;
      case 'cargo': this.cargoTab(content, d); break;
      case 'upkeep': this.upkeep(content, d); break;
      case 'estimator': this.estimatorInto(content, d); break;
      case 'reports': this.reports(content, d); break;
      case 'log': this.log(content, d); break;
    }
    return content;
  }

  /** Rebuild the tab's content in place; false = not now (the player is typing in it — try again next tick). */
  private rebuildContent(d: DroidRowLike | null): boolean {
    const old = this.win?.body.querySelector<HTMLDivElement>('[data-role="content"]');
    if (!old) return true;
    // THE EDITOR IS NEVER REBUILT UNDER THE PLAYER'S CURSOR: the Program tab refreshes its status and gate in place.
    if (d && this.tab === 'program' && old.querySelector('textarea.dd-src')) return true;
    if (this.editing(old)) return false;
    const snap = this.snapshot(old);
    const fresh = this.content(d);
    old.replaceWith(fresh);
    this.restore(fresh, snap);
    return true;
  }

  /** The player is typing inside `root` (an input, a text box or a select holds the focus). */
  private editing(root: HTMLElement): boolean {
    const a = document.activeElement as HTMLElement | null;
    return !!a && root.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable);
  }

  private snapshot(root: HTMLElement): Snap {
    const scroll: Snap['scroll'] = [];
    if (root.scrollTop || root.scrollLeft) scroll.push(['@root', root.scrollTop, root.scrollLeft]);
    for (const e of Array.from(root.querySelectorAll<HTMLElement>('[data-scroll]'))) {
      if (e.scrollTop || e.scrollLeft) scroll.push([e.dataset.scroll!, e.scrollTop, e.scrollLeft]);
    }
    const values = new Map<string, string>();
    for (const e of Array.from(root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-k][data-edited]'))) values.set(e.dataset.k!, e.value);
    const a = document.activeElement as HTMLElement | null;
    const focus = a && root.contains(a) ? a.dataset.k ?? null : null;
    let caret: [number, number] | null = null;
    if (focus && (a instanceof HTMLTextAreaElement || a instanceof HTMLInputElement)) {
      try { if (a.selectionStart !== null && a.selectionEnd !== null) caret = [a.selectionStart, a.selectionEnd]; } catch { /* number inputs have no caret */ }
    }
    return { scroll, values, focus, caret };
  }

  private restore(root: HTMLElement, s: Snap): void {
    const byKey = (k: string): HTMLElement | null => root.querySelector<HTMLElement>(`[data-k="${k}"]`);
    for (const [k, v] of s.values) {
      const e = byKey(k) as HTMLInputElement | null;
      if (!e || !('value' in e)) continue;
      e.dataset.edited = '1';
      // Its own listeners hear the put-back value (the estimator re-runs on it, the editor re-checks it).
      if (e.value !== v) { e.value = v; e.dispatchEvent(new Event('input', { bubbles: true })); }
    }
    if (s.focus) {
      const e = byKey(s.focus);
      if (e) {
        e.focus({ preventScroll: true });
        if (s.caret && (e instanceof HTMLTextAreaElement || e instanceof HTMLInputElement)) { try { e.setSelectionRange(s.caret[0], s.caret[1]); } catch { /* no caret */ } }
      }
    }
    for (const [k, top, left] of s.scroll) {
      const e = k === '@root' ? root : root.querySelector<HTMLElement>(`[data-scroll="${k}"]`);
      if (e) { e.scrollTop = top; e.scrollLeft = left; }
    }
  }

  /** The rail's and the header's data: every droid you own (mode, health, pool, distance), the bag's droid items,
   *  and the selected droid's program status and position. */
  private chromeSignature(d: DroidRowLike | null): string {
    const parts: string[] = [];
    for (const m of this.mine()) {
      const v = this.vitals(m.droidId);
      parts.push(`${m.droidId}:${m.name}:${m.mode}:${Math.round(v?.hp ?? 0)}/${Math.round(v?.maxHp ?? 0)}:${this.money(m.droidId).pool}:${Math.round(this.distTo(m))}`);
    }
    const c = this.conn();
    if (c?.identity) { try { for (const it of c.db.playerItem.iter()) if (it.itemKey.startsWith('droid_') && it.owner.isEqual(c.identity)) parts.push(`${it.itemId}`); } catch { /* churn */ } }
    if (d) {
      const b = this.brain(d.droidId);
      const p = this.pos(d);
      parts.push(`${d.name}:${b?.status ?? ''}:${b?.line ?? ''}:${b?.err ?? ''}:${Math.round(p.x)},${Math.round(p.z)}`);
    }
    return parts.join('|');
  }

  /** The open tab's CONTENT data — what the tab builds from (live numbers and gates are refreshed by `live`). */
  private bodySignature(d: DroidRowLike | null): string {
    if (!d) return 'none';
    switch (this.tab) {
      case 'orders': return `${d.kind}:${d.quality}`;
      case 'cargo': {
        const parts: string[] = [];
        for (const e of this.cargo(d.droidId)) parts.push(`${e.entryId}:${e.qty}:${e.priceSov}`);
        parts.push(String(this.bagSig()));
        return parts.join('|');
      }
      case 'reports': {
        const parts: string[] = [String(this.reportSel ?? '')];
        const c = this.conn();
        if (c) for (const r of c.db.droidReport.iter()) parts.push(String(r.reportId));
        return parts.join('|');
      }
      case 'log': { const lj = this.logJsonOf(d); return `${lj.length}:${lj.slice(-64)}`; }
      default: return d.droidId.toString(); // program, upkeep, estimator: their live parts refresh in place
    }
  }

  private bagSig(): number {
    const c = this.conn();
    if (!c?.identity) return 0;
    let n = 0;
    try { for (const i of c.db.playerItem.iter()) if (i.owner.isEqual(c.identity)) n += 1; for (const s of c.db.playerResource.iter()) if (s.owner.isEqual(c.identity)) n += s.qty; } catch { /* churn */ }
    return n;
  }

  /** Patch the rail and the header in place (neither holds a text field). The rail's numbers are patched; a rail whose
   *  cards or bag lines changed is rebuilt keeping its scroll and the focused card or Deploy button. */
  private refreshChrome(d: DroidRowLike | null): void {
    const body = this.win?.body;
    if (!body) return;
    const rail = body.querySelector<HTMLDivElement>('[data-role="rail"]');
    if (rail && !this.patchRail(rail)) {
      const snap = this.snapshot(rail);
      rail.textContent = '';
      this.fillRail(rail);
      this.restore(rail, snap);
    }
    const old = body.querySelector('[data-role="dd-header"]');
    if (d && old) {
      // The header's two lines are patched in place (the mode, the credits, the distance).
      const fresh = this.header(d);
      const on = old.querySelector('.n'), om = old.querySelector('.m'), fn = fresh.querySelector('.n'), fm = fresh.querySelector('.m');
      if (on && om && fn && fm) {
        if (on.textContent !== fn.textContent) on.textContent = fn.textContent;
        if (om.textContent !== fm.textContent) om.textContent = fm.textContent;
      } else { fresh.dataset.role = 'dd-header'; old.replaceWith(fresh); }
    }
    const title = d ? `Droids — ${d.name}` : 'Droids';
    if (this.win!.root.querySelector('[data-ax-title]')?.textContent !== title) this.win!.setTitle(title);
  }

  /** The droid items in your bag (the rail offers to deploy them). */
  private bagDroids(): Array<{ itemId: bigint; itemKey: string; name: string; quality: number }> {
    const c = this.conn();
    const bag: Array<{ itemId: bigint; itemKey: string; name: string; quality: number }> = [];
    if (c?.identity) {
      try { for (const it of c.db.playerItem.iter()) if (it.owner.isEqual(c.identity) && it.itemKey.startsWith('droid_')) bag.push(it); } catch { /* churn */ }
    }
    return bag;
  }

  /** What the rail is MADE of — its cards, its bag lines and the selection; everything else on it is patched in place. */
  private railStruct(): string {
    return `${this.mine().map((d) => `${d.droidId}:${d.name}`).join(',')}|${this.bagDroids().map((it) => `${it.itemId}`).sort().join(',')}|${this.sel ?? ''}`;
  }

  /** A card's changing lines: the class and mode, the health bar, the pool and the distance. */
  private cardLines(d: DroidRowLike): { mode: string; pct: string; bar: string; pool: string } {
    const v = this.vitals(d.droidId);
    const hpFrac = v ? v.hp / Math.max(1, v.maxHp) : 1;
    const dist = this.distTo(d);
    return {
      mode: `${DROID_CLASSES[classOfKind(d.kind)].name} · ${d.mode}`,
      pct: `${Math.round(hpFrac * 100)}%`,
      bar: `dd-bar${hpFrac < 0.25 ? ' low' : hpFrac < 0.6 ? ' hurt' : ''}`,
      pool: `${credits(Number(this.money(d.droidId).pool) / MILLI)} credits · ${dist < 1e6 ? `${Math.round(dist)} m` : 'elsewhere'}`,
    };
  }

  /** Patch the rail's numbers in place (2026-09-25: it was rebuilt every second while a droid worked — a click on a
   *  card could land on a node that had just been replaced). False when its structure changed: rebuild it. */
  private patchRail(rail: HTMLDivElement): boolean {
    if (rail.dataset.struct !== this.railStruct()) return false;
    for (const d of this.mine()) {
      const card = rail.querySelector<HTMLElement>(`[data-droid-id="${d.droidId}"]`);
      const mode = card?.querySelector<HTMLElement>('[data-f="mode"]'), bar = card?.querySelector<HTMLElement>('[data-f="bar"]');
      const fill = bar?.firstElementChild as HTMLElement | null | undefined, pool = card?.querySelector<HTMLElement>('[data-f="pool"]');
      if (!mode || !bar || !fill || !pool) return false;
      const l = this.cardLines(d);
      if (mode.textContent !== l.mode) mode.textContent = l.mode;
      if (bar.className !== l.bar) bar.className = l.bar;
      if (fill.style.width !== l.pct) fill.style.width = l.pct;
      if (pool.textContent !== l.pool) pool.textContent = l.pool;
    }
    return true;
  }

  private fillRail(rail: HTMLDivElement): void {
    rail.dataset.struct = this.railStruct();
    rail.appendChild(this.el('div', 'ax-head', 'Deployed'));
    const mine = this.mine();
    if (mine.length === 0) rail.appendChild(this.el('div', 'dd-note', 'None yet.'));
    for (const d of mine) {
      const l = this.cardLines(d);
      const card = this.el('div', `dd-card${d.droidId === this.sel ? ' on' : ''}`);
      card.dataset.droidId = d.droidId.toString();
      const mode = this.el('div', 's', l.mode);
      mode.dataset.f = 'mode';
      card.append(this.el('div', 't', d.name), mode);
      const bar = this.el('div', l.bar);
      bar.dataset.f = 'bar';
      const fill = this.el('i');
      fill.style.width = l.pct;
      bar.appendChild(fill);
      card.appendChild(bar);
      const pool = this.el('div', 's', l.pool);
      pool.dataset.f = 'pool';
      card.appendChild(pool);
      card.onclick = () => { this.sel = d.droidId; this.render(true); };
      // A card is a control (2026-09-25): a plain div was invisible to the pad's navigator and to Tab, so a pad player
      // could never pick another droid.
      card.tabIndex = 0;
      card.setAttribute('role', 'button');
      card.dataset.k = `card:${d.droidId}`;
      card.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); } };
      rail.appendChild(card);
    }
    // The bag's droids: deploy from the datapad (the Commons, at your feet).
    const c = this.conn();
    const bag = this.bagDroids();
    rail.appendChild(this.el('div', 'ax-head', 'In your bag'));
    if (bag.length === 0) rail.appendChild(this.el('div', 'dd-note', 'No droid items — craft one at the Workbench (F6).'));
    // Identical droids (same design, same quality) share one line with a count.
    const groups = new Map<string, { first: typeof bag[number]; n: number }>();
    for (const it of bag) {
      const k = `${it.itemKey}|${it.quality}`;
      const g = groups.get(k);
      if (g) g.n++; else groups.set(k, { first: it, n: 1 });
    }
    for (const { first: it, n } of [...groups.values()].slice(0, 12)) {
      const row = this.el('div', 'dd-row');
      const dep = this.btn('Deploy', () => this.call(() => c?.reducers.deployDroid({ itemId: it.itemId }), `${it.name} deployed`));
      dep.dataset.k = `deploy:${it.itemKey}:${it.quality}`;
      row.append(this.el('span', 'dd-grow', `${it.name} (q${it.quality})${n > 1 ? ` ×${n}` : ''}`), dep);
      rail.appendChild(row);
    }
  }

  private header(d: DroidRowLike): HTMLElement {
    const h = this.el('div', 'dd-head');
    const cls = classOfKind(d.kind);
    const v = this.vitals(d.droidId);
    const b = this.brain(d.droidId);
    h.append(this.el('div', 'n', d.name));
    const dist = this.distTo(d);
    const status = b ? (b.status === 'error' ? `error — ${b.err}` : b.status === 'broke' ? 'out of credits' : b.status === 'done' ? 'program finished' : `program line ${b.line}`) : 'no program';
    h.append(this.el('div', 'm',
      `${DROID_CLASSES[cls].name} · q${d.quality} · ${d.mode} · ${status} · health ${Math.round(v?.hp ?? 0)}/${Math.round(v?.maxHp ?? 0)} · ${credits(Number(this.money(d.droidId).pool) / MILLI)} credits · at (${Math.round(this.pos(d).x)}, ${Math.round(this.pos(d).z)}) · ${dist < 1e6 ? `${Math.round(dist)} m from you` : 'in another world'}`));
    return h;
  }

  // ─── Orders ────────────────────────────────────────────────────────────────

  private orders(el: HTMLElement, d: DroidRowLike): void {
    const c = this.conn();
    const id = d.droidId;
    const cmd = (x: string, msg: string) => () => this.call(() => c?.reducers.droidCommand({ droidId: id, cmd: x }), msg);
    el.appendChild(this.el('div', 'ax-head', 'Orders'));
    const row = this.el('div', 'dd-row');
    row.append(
      this.btn('Run', cmd('run', 'Running'), true, 'Run (or resume) the loaded program'),
      this.btn('Stop', cmd('stop', 'Holding'), false, 'Hold position (the running cost stops)'),
      this.btn('Restart', cmd('restart', 'Restarted'), false, 'Run the program again from the top'),
      this.btn('Come', cmd('come', 'Coming to you')),
      this.btn('Follow', cmd('follow', 'Following you')),
      this.btn('Home', cmd('home', 'Going home')),
      this.btn('Scan', cmd('scan', 'Scanned')),
      this.btn('Scan life', cmd('scanlife', 'Scanned')),
      this.btn('Recall', () => { if (window.confirm(`Recall ${d.name}? Its cargo comes back to your bag and the unspent credits are refunded.`)) this.call(() => c?.reducers.recallDroid({ droidId: id }), 'Recalled'); }, false, 'Pack the droid back into your bag'),
    );
    el.appendChild(row);
    el.appendChild(this.el('p', 'dd-note', 'Orders work from anywhere (your datapad). Programs, cargo and repairs need you at the droid.'));
    const cls = classOfKind(d.kind);
    const spec = DROID_CLASSES[cls];
    const st = droidStats(cls, d.quality);
    el.appendChild(this.el('div', 'ax-head', spec.name));
    el.appendChild(this.el('p', '', spec.blurb));
    const kv = this.el('div', 'dd-kv');
    const add = (k: string, v: string): void => { kv.append(this.el('span', 'k', k), this.el('span', 'ax-num', v)); };
    add('Speed', `${st.speed.toFixed(1)} m/s`);
    add('Health', `${st.maxHp}`);
    add('Cargo', `${spec.cargoSlots} slots · ${spec.cargoUnits} units`);
    add('Sensors', `${spec.scanRangeM} m`);
    add('Leash', `${spec.leashM} m from home`);
    if (st.attackDmg > 0) add('Attack', `${st.attackDmg} a strike · ${spec.attackRangeM} m${spec.splashM ? ` · ${spec.splashM} m splash` : ''}`);
    if (st.healPerAct > 0) add('Heal', `${st.healPerAct} a treatment`);
    add('Running cost', `${credits(st.runPerHour)} credits an hour while working`);
    add('Action prices', `×${st.opMult.toFixed(2)}`);
    el.appendChild(kv);
    const caps = this.el('div', 'dd-row');
    for (const cap of spec.caps) caps.appendChild(this.el('span', 'ax-chip', cap));
    el.appendChild(caps);
  }

  // ─── Program ───────────────────────────────────────────────────────────────

  private program(el: HTMLElement, d: DroidRowLike): void {
    const c = this.conn();
    const cls = classOfKind(d.kind);
    const key = d.droidId.toString();
    const src = this.srcDraft.get(key) ?? (this.source(d) || templatesFor(cls)[0]?.source || 'say "Hello"');
    const top = this.el('div', 'dd-row');
    const sel = this.el('select', 'ax-input');
    sel.dataset.k = 'template';
    sel.style.width = '220px';
    const o0 = this.el('option', '', 'Load a prebuilt program…');
    o0.value = '';
    sel.appendChild(o0);
    for (const t of templatesFor(cls)) { const o = this.el('option', '', t.name); o.value = t.key; o.title = t.blurb; sel.appendChild(o); }
    top.append(sel, this.el('span', 'dd-note dd-grow', ''));
    el.appendChild(top);
    const ed = this.el('div', 'dd-ed');
    const gut = this.el('div', 'dd-gut');
    const ta = this.el('textarea', 'dd-src');
    ta.spellcheck = false;
    ta.value = src;
    ta.dataset.k = 'src';
    ed.append(gut, ta);
    el.appendChild(ed);
    const status = this.el('div', 'dd-row');
    status.dataset.role = 'program-status';
    el.appendChild(status);
    const act = this.el('div', 'dd-row');
    const up = this.btn('Upload & run', () => this.call(() => c?.reducers.droidProgram({ droidId: d.droidId, source: ta.value }), 'Program uploaded — running'), true);
    const callHere = this.btn('Call it here', () => this.call(() => c?.reducers.droidCommand({ droidId: d.droidId, cmd: 'come' }), 'Coming to you'));
    callHere.title = 'Order the droid to walk to you (the terminal opens when it is in reach)';
    act.append(up, this.btn('Restart', () => this.call(() => c?.reducers.droidCommand({ droidId: d.droidId, cmd: 'restart' }), 'Restarted')),
      this.btn('Estimate the cost', () => { this.srcDraft.set(key, ta.value); this.tab = 'estimator'; this.render(true); }), callHere);
    el.appendChild(act);
    const reachNote = this.el('p', 'dd-note dd-reach');
    el.appendChild(reachNote);
    // THE UPLOAD GATE IS LIVE (2026-09-24): this tab is never rebuilt under the player's cursor, so a gate computed
    // once stayed shut after the player walked up (only a tab switch reopened it). It re-reads the droid's row and
    // the distance on every refresh — reach measured from the HULL, like the server.
    // Writes only what changed (2026-09-25): this runs every tick, and each needless write was a DOM mutation that made
    // the pad's navigator re-read the panel.
    const gate = (): void => {
      const dd = this.droid() ?? d;
      const dist = this.distTo(dd), reach = this.reach(dd);
      const near = dist <= reach;
      if (up.disabled !== !near) up.disabled = !near;
      const tip = near ? '' : `Stand within ${Math.round(reach)} m of the droid to upload`;
      if (up.title !== tip) up.title = tip;
      const show = near || !Number.isFinite(dist) ? 'none' : '';
      if (callHere.style.display !== show) callHere.style.display = show;
      const note = near ? ''
        : !Number.isFinite(dist) ? 'The droid is in another world — upload from beside it.'
        : `Out of reach: you are ${Math.round(dist)} m from it, and the terminal works within ${Math.round(reach)} m. Walk over, or call it here. (Credits are not the problem — the upload is done at the droid.)`;
      if (reachNote.textContent !== note) reachNote.textContent = note;
    };
    gate();
    // The API reference.
    el.appendChild(this.el('div', 'ax-head', 'Reference — click a row to insert its example'));
    const filt = this.el('input', 'ax-input');
    filt.placeholder = 'Search commands, sensors, events…';
    filt.style.width = '260px';
    filt.value = this.apiFilter;
    filt.dataset.k = 'api-filter';
    el.appendChild(filt);
    const api = this.el('div', 'dd-api');
    api.dataset.scroll = 'api';
    el.appendChild(api);
    const drawApi = (): void => {
      api.textContent = '';
      const tb = this.el('table');
      const q = this.apiFilter.toLowerCase();
      let group = '';
      for (const r of apiReference(cls)) {
        if (q && !`${r.sig} ${r.doc} ${r.group}`.toLowerCase().includes(q)) continue;
        if (r.group !== group) {
          group = r.group;
          const tr = this.el('tr');
          const th = this.el('th', '', group);
          th.colSpan = 3;
          tr.appendChild(th);
          tb.appendChild(tr);
        }
        const tr = this.el('tr', r.available ? '' : 'off');
        const c1 = this.el('td');
        c1.appendChild(this.el('code', '', r.sig));
        tr.append(c1, this.el('td', '', r.available ? r.doc : `${r.doc} (not this class)`), this.el('td', 'num', r.cost));
        tr.onclick = () => {
          const ins = r.example ? r.example.split('·')[0].trim() : r.sig.split('→')[0].trim();
          const at = ta.selectionStart ?? ta.value.length;
          const before = ta.value.slice(0, at);
          const nl = before.length && !before.endsWith('\n') ? '\n' : '';
          ta.value = `${before}${nl}${ins}\n${ta.value.slice(at)}`;
          ta.focus();
          check();
        };
        tb.appendChild(tr);
      }
      api.appendChild(tb);
    };
    filt.oninput = () => { this.apiFilter = filt.value; drawApi(); };
    drawApi();
    // Live checking.
    let errLine = 0;
    let gutKey = '';
    const drawGutter = (): void => {
      const n = ta.value.split('\n').length;
      const b = this.brain(d.droidId);
      const cur = b && b.status === 'running' ? b.line : 0;
      // The ▸ marks the running line only while the editor holds the UPLOADED program (droid_source, 2026-09-25).
      const running = cur > 0 && ta.value === this.source(this.droid() ?? d) ? cur : 0;
      // Redrawn only when it would change (this runs every tick and on every keystroke).
      const k = `${n}|${errLine}|${running}`;
      if (k !== gutKey) {
        gutKey = k;
        gut.textContent = '';
        for (let i = 1; i <= n; i++) {
          if (i > 1) gut.appendChild(document.createTextNode('\n'));
          if (i === errLine) gut.appendChild(this.el('b', '', String(i)));
          else if (i === running) gut.appendChild(this.el('u', '', `▸${i}`));
          else gut.appendChild(document.createTextNode(String(i)));
        }
      }
      gut.scrollTop = ta.scrollTop;
    };
    const check = (): void => {
      this.srcDraft.set(key, ta.value);
      // ONE COMPILE (2026-09-25): estimateDroid compiles the program and returns the compiler's errors and warnings;
      // the check compiled it a second time first.
      const e = estimateDroid(ta.value, { cls, quality: d.quality, homeX: d.homeX, homeZ: d.homeZ, complexityOf: (k) => SCHEMATIC_BY_KEY[k]?.complexity ?? 2 });
      status.textContent = '';
      errLine = 0;
      if (!e.ok) {
        const m = /^Line (\d+):/.exec(e.errors[0] ?? '');
        errLine = m ? Number(m[1]) : 0;
        status.appendChild(this.el('span', 'dd-err', `✗ ${e.errors[0] ?? 'does not compile'}`));
      } else {
        status.appendChild(this.el('span', 'dd-okc', `✓ compiles · about ${credits(e.perHour)} credits an hour${e.loops ? ` · one pass ${credits(e.passCost)} in ${Math.round(e.passSecs)} s` : ` · the whole run about ${credits(e.oneShotCost)}`}`));
        for (const w of e.warnings) status.appendChild(this.el('span', 'dd-warn', `⚠ ${w}`));
      }
      drawGutter();
    };
    let deb: number | null = null;
    // The draft is kept at once (a tab switch inside the debounce lost the last keystrokes); the check waits 250 ms.
    ta.oninput = () => { this.srcDraft.set(key, ta.value); if (deb !== null) window.clearTimeout(deb); deb = window.setTimeout(check, 250); drawGutter(); };
    ta.onscroll = () => { gut.scrollTop = ta.scrollTop; };
    ta.onkeydown = (e) => {
      e.stopPropagation(); // typing never drives the avatar
      if (e.key === 'Tab') { e.preventDefault(); const s = ta.selectionStart; ta.value = `${ta.value.slice(0, s)}  ${ta.value.slice(ta.selectionEnd)}`; ta.selectionStart = ta.selectionEnd = s + 2; }
    };
    sel.onchange = () => {
      const t = templatesFor(cls).find((x) => x.key === sel.value);
      if (!t) return;
      ta.value = t.source;
      sel.value = '';
      check();
    };
    // Every tick: the running-line marker and the upload gate (the header is the chrome's, refreshed by `render`).
    this.live = () => { drawGutter(); gate(); };
    check();
  }

  // ─── Cargo ─────────────────────────────────────────────────────────────────

  private cargoTab(el: HTMLElement, d: DroidRowLike): void {
    const c = this.conn();
    const cls = classOfKind(d.kind);
    const spec = DROID_CLASSES[cls];
    const rows = this.cargo(d.droidId);
    let units = 0;
    for (const e of rows) if (e.kind === 'stack') units += e.qty;
    // LIVE REACH (2026-09-25): the reach note and the hands-on buttons follow the player's distance every tick in place
    // (the distance used to be in the tab's signature, so walking rebuilt the tab and wiped a price being typed).
    const handsOn: HTMLButtonElement[] = [];
    const reachNote = this.el('p', 'dd-note dd-reach');
    el.appendChild(this.el('div', 'ax-head', `Cargo — ${units}/${spec.cargoUnits} units · ${rows.length}/${spec.cargoSlots} slots`));
    el.appendChild(reachNote);
    if (rows.length === 0) el.appendChild(this.el('p', 'dd-note', 'Empty.'));
    else {
      const tb = this.el('table');
      const hr = this.el('tr');
      for (const h of ['Goods', 'Qty', 'Price (per unit)', '']) hr.appendChild(this.el('th', '', h));
      tb.appendChild(hr);
      for (const e of rows) {
        const tr = this.el('tr');
        tr.append(this.el('td', '', e.kind === 'item' ? `${e.name} (q${e.quality})` : e.name), this.el('td', 'num', String(e.qty)));
        const pc = this.el('td');
        const pi = this.num(Number(e.priceSov), 0, 1_000_000, 1, `price:${e.entryId}`);
        const setPrice = (): void => {
          const price = whole(pi.value, 0, 0, 1_000_000);
          this.call(() => c?.reducers.droidSetPrice({ droidId: d.droidId, entryId: e.entryId, priceSov: BigInt(price) }), price > 0 ? 'Priced' : 'Off sale');
        };
        pc.append(pi, this.btn('Set', setPrice, false, '', `set:${e.entryId}`));
        tr.appendChild(pc);
        const uc = this.el('td');
        const qi = this.num(e.qty, 1, e.qty, 1, `uq:${e.entryId}`);
        if (e.kind === 'item') qi.style.display = 'none';
        const ub = this.btn('Unload', () => {
          const q = whole(qi.value, e.qty, 0, e.qty);
          this.call(() => c?.reducers.droidUnload({ droidId: d.droidId, entryId: e.entryId, qty: q > 0 ? q : e.qty }), 'Unloaded');
        }, false, '', `unload:${e.entryId}`);
        handsOn.push(ub);
        uc.append(qi, ub);
        tr.appendChild(uc);
        tb.appendChild(tr);
      }
      el.appendChild(tb);
    }
    el.appendChild(this.el('div', 'ax-head', 'Load from your bag'));
    const paintReach = (): void => {
      const dd = this.droid() ?? d;
      const dist = this.distTo(dd);
      const near = dist <= this.reach(dd);
      for (const b of handsOn) if (b.disabled !== !near) b.disabled = !near;
      const note = near ? '' : `Walk to the droid (within ${Math.round(this.reach(dd))} m; ${Number.isFinite(dist) ? `you are ${Math.round(dist)} m away` : 'it is in another world'}) to load and unload. Prices can be set from anywhere.`;
      if (reachNote.textContent !== note) reachNote.textContent = note;
      const show = near ? 'none' : '';
      if (reachNote.style.display !== show) reachNote.style.display = show;
    };
    this.live = paintReach;
    if (!c?.identity) { paintReach(); return; }
    const stacks = [...c.db.playerResource.iter()].filter((s) => s.owner.isEqual(c.identity!)).sort((a, b) => b.qty - a.qty).slice(0, 30);
    const items = [...c.db.playerItem.iter()].filter((i) => i.owner.isEqual(c.identity!) && !i.itemKey.startsWith('droid_')).slice(0, 30);
    const tb = this.el('table');
    for (const s of stacks) {
      const tr = this.el('tr');
      const qi = this.num(s.qty, 1, s.qty, 1, `lq:${s.stackId}`);
      const b = this.btn('Load', () => {
        const q = whole(qi.value, s.qty, 0, s.qty);
        this.call(() => c.reducers.droidLoadStack({ droidId: d.droidId, stackId: s.stackId, qty: q > 0 ? q : s.qty }), `Loaded ${s.name}`);
      }, false, '', `loads:${s.stackId}`);
      handsOn.push(b);
      const tdq = this.el('td');
      tdq.append(qi, b);
      tr.append(this.el('td', '', s.name), this.el('td', 'num', String(s.qty)), tdq);
      tb.appendChild(tr);
    }
    for (const it of items) {
      const tr = this.el('tr');
      const b = this.btn('Load', () => this.call(() => c.reducers.droidLoadItem({ droidId: d.droidId, itemId: it.itemId }), `Loaded ${it.name}`), false, '', `loadi:${it.itemId}`);
      handsOn.push(b);
      const tdb = this.el('td');
      tdb.appendChild(b);
      tr.append(this.el('td', '', `${it.name} (q${it.quality})`), this.el('td', 'num', '1'), tdb);
      tb.appendChild(tr);
    }
    if (stacks.length + items.length === 0) el.appendChild(this.el('p', 'dd-note', 'Your bag is empty.'));
    else el.appendChild(tb);
    paintReach();
  }

  // ─── Upkeep ────────────────────────────────────────────────────────────────

  private upkeep(el: HTMLElement, d: DroidRowLike): void {
    const c = this.conn();
    const cls = classOfKind(d.kind);
    // LIVE NUMBERS (2026-09-25): the pool drains every second while the droid works, and it was in the tab's signature —
    // the whole tab was rebuilt each tick and a Fund / Withdraw amount being typed reset to 100 / 0. The numbers are
    // spans painted in place by `paint`; the fields are built once.
    el.appendChild(this.el('div', 'ax-head', 'Maintenance pool'));
    const big = this.el('div', 'dd-big');
    el.appendChild(big);
    const kv = this.el('div', 'dd-kv');
    const add = (k: string): HTMLSpanElement => { const s = this.el('span', 'ax-num'); kv.append(this.el('span', 'k', k), s); return s; };
    const bonusV = add('Starter charge left'), refundV = add('Refundable'), spentV = add('Spent so far');
    const fundedV = add('Funded so far'), salesV = add('Takings (vending)'), rateV = add('At its current rate');
    el.appendChild(kv);
    const row = this.el('div', 'dd-row');
    const fi = this.num(100, 1, 1_000_000, 1, 'fund');
    const walletV = this.el('span', 'dd-note');
    row.append(this.el('span', '', 'Fund'), fi, this.btn('Fund', () => {
      const amount = whole(fi.value, 0, 1, Number.MAX_SAFE_INTEGER);
      this.call(() => c?.reducers.droidFund({ droidId: d.droidId, amount: BigInt(amount) }), 'Funded');
    }, true, '', 'fund-go'), walletV);
    el.appendChild(row);
    const row2 = this.el('div', 'dd-row');
    const wi = this.num(0, 0, 1_000_000, 1, 'withdraw');
    row2.append(this.el('span', '', 'Withdraw'), wi, this.btn('Withdraw', () => {
      const amount = whole(wi.value, 0, 0, Number.MAX_SAFE_INTEGER);
      this.call(() => c?.reducers.droidDefund({ droidId: d.droidId, amount: BigInt(amount) }), 'Withdrawn');
    }, false, '', 'withdraw-go'),
      this.el('span', 'dd-note', '0 = everything refundable'));
    el.appendChild(row2);
    el.appendChild(this.el('div', 'ax-head', 'Health'));
    const hpV = this.el('div');
    el.appendChild(hpV);
    const rep = this.btn('Repair', () => this.call(() => c?.reducers.droidRepair({ droidId: d.droidId }), 'Repaired'), false, '', 'repair');
    el.appendChild(rep);
    el.appendChild(this.el('p', 'dd-note', 'A medical droid (or a humanoid) nearby can heal a droid too: its program runs `heal droid`.'));
    el.appendChild(this.el('p', 'dd-note', `A program's \`upkeep\` pays your structures from YOUR wallet, not this pool — at most ${UPKEEP_CALL_MAX} credits a call and ${UPKEEP_HOUR_CAP} an hour per droid.`));
    const put = (e: HTMLElement, t: string): void => { if (e.textContent !== t) e.textContent = t; };
    const paint = (): void => {
      const dd = this.droid() ?? d;
      const v = this.vitals(dd.droidId); // health (public)
      const m = this.money(dd.droidId); // the owner's purse (2026-09-28)
      const pool = Number(m.pool) / MILLI;
      put(big, `${credits(pool)} credits`);
      put(bonusV, `${credits(Number(m.bonus) / MILLI)} (spent first, never refunded)`);
      put(refundV, `${refundableCredits(Number(m.pool), Number(m.bonus))} credits`);
      put(spentV, `${credits(Number(m.spent) / MILLI)} credits`);
      put(fundedV, `${credits(Number(m.funded))} credits`);
      put(salesV, `${credits(Number(m.sales))} credits`);
      const { perHour, basis } = this.rateOf(dd, cls);
      put(rateV, `about ${credits(perHour)} an hour (${basis}) — the pool lasts ${duration(runtimeHours(pool, perHour))}`);
      const wallet = c?.identity ? Number(c.db.wallet.identity.find(c.identity)?.sov ?? 0n) : 0;
      put(walletV, `your wallet: ${credits(wallet)} credits`);
      const hp = v ? v.hp : 0, max = v ? v.maxHp : 1;
      put(hpV, `${Math.round(hp)}/${Math.round(max)}${dd.mode === 'down' ? ' — DISABLED' : ''}`);
      put(rep, `Repair (${Math.max(0, Math.ceil((max - hp) / 2))} credits)`);
      const near = this.isNear(dd);
      const off = (hp >= max && dd.mode !== 'down') || !near;
      if (rep.disabled !== off) rep.disabled = off;
      const tip = near ? '' : `Stand within ${Math.round(this.reach(dd))} m of the droid to repair it`;
      if (rep.title !== tip) rep.title = tip;
    };
    this.live = paint;
    paint();
  }

  /** The hourly rate the Upkeep tab shows: the uploaded program's estimate, else the running cost alone. Memoised —
   *  the estimate compiles the program, and the tab repaints every second. */
  private rateOf(d: DroidRowLike, cls: DroidClass): { perHour: number; basis: string } {
    const st = droidStats(cls, d.quality);
    const src = this.source(d);
    if (!src) return { perHour: st.runPerHour, basis: 'the running cost alone' };
    const key = `${cls}|${d.quality}|${d.homeX}|${d.homeZ}|${src}`;
    if (this.rateMemo.key !== key) {
      const e = estimateDroid(src, { cls, quality: d.quality, homeX: d.homeX, homeZ: d.homeZ, complexityOf: (k) => SCHEMATIC_BY_KEY[k]?.complexity ?? 2 });
      this.rateMemo = { key, perHour: e.perHour, ok: e.ok };
    }
    return this.rateMemo.ok ? { perHour: this.rateMemo.perHour, basis: 'its program' } : { perHour: st.runPerHour, basis: 'the running cost alone' };
  }

  // ─── Estimator ─────────────────────────────────────────────────────────────

  private estimatorInto(el: HTMLElement, d: DroidRowLike | null): void {
    const clsDefault: DroidClass = d ? classOfKind(d.kind) : 'supply';
    if (!this.estOpts.cls) this.estOpts.cls = clsDefault;
    if (d && this.estOpts.quality === 0) this.estOpts.quality = d.quality;
    if (this.estOpts.quality === 0) this.estOpts.quality = 500;
    const key = d?.droidId.toString() ?? '';
    // The editor's draft, else the uploaded program (droid_source first, 2026-09-25), else an example — `??` let a new
    // row's EMPTY programSrc through, and the estimator opened on a blank program.
    const src = (d && this.srcDraft.get(key)) || (d && this.source(d)) || 'patrol 60\nscan life\nwait 30\nloop';
    el.appendChild(this.el('div', 'ax-head', 'Cost estimator'));
    el.appendChild(this.el('p', 'dd-note', 'Harder work costs more (every action has a price, multiplied by the class), and longer work costs more (the hourly running cost). Paste any program — or use the one in the editor.'));
    const form = this.el('div', 'dd-row');
    const clsSel = this.el('select', 'ax-input');
    clsSel.dataset.k = 'est-cls';
    clsSel.style.width = '170px';
    for (const k of DROID_CLASS_ORDER) { const o = this.el('option', '', DROID_CLASSES[k].name); o.value = k; if (k === this.estOpts.cls) o.selected = true; clsSel.appendChild(o); }
    const q = this.num(this.estOpts.quality, 1, 1000, 1, 'est-q');
    const hours = this.num(this.estOpts.hours, 0.1, 10000, 0.5, 'est-h');
    const poolDefault = this.estOpts.pool >= 0 ? this.estOpts.pool : Math.round(Number(d ? this.money(d.droidId).pool : 100_000n) / MILLI);
    const pool = this.num(poolDefault, 0, 1_000_000, 1, 'est-pool');
    const lg = this.num(this.estOpts.loopGuess, 1, 1000, 1, 'est-loops');
    const ev = this.num(this.estOpts.eventsPerHour, 0, 3600, 1, 'est-events');
    const lab = (t: string, e: HTMLElement): HTMLElement => { const s = this.el('label', 'dd-row'); s.style.margin = '0'; s.append(this.el('span', 'dd-note', t), e); return s; };
    form.append(lab('Class', clsSel), lab('Quality', q), lab('Hours', hours), lab('Pool', pool), lab('Loops run', lg), lab('Events / h', ev));
    el.appendChild(form);
    const ta = this.el('textarea', 'ax-input');
    ta.style.cssText = 'width:100%;height:110px;font:12px/1.45 var(--ax-mono);box-sizing:border-box';
    ta.value = src;
    ta.spellcheck = false;
    ta.dataset.k = 'est-src';
    ta.onkeydown = (e) => e.stopPropagation();
    el.appendChild(ta);
    const out = this.el('div');
    out.dataset.role = 'estimate';
    el.appendChild(out);
    const run = (): void => {
      this.estOpts = {
        cls: clsSel.value as DroidClass, quality: Math.max(1, Math.min(1000, Number(q.value) || 500)),
        hours: Math.max(0, Number(hours.value) || 0), pool: Math.max(0, Number(pool.value) || 0),
        loopGuess: Math.max(1, Number(lg.value) || 3), eventsPerHour: Math.max(0, Number(ev.value) || 0),
      };
      const o = this.estOpts;
      const opts = { quality: o.quality, homeX: d?.homeX, homeZ: d?.homeZ, loopGuess: o.loopGuess, eventsPerHour: o.eventsPerHour, complexityOf: (k: string) => SCHEMATIC_BY_KEY[k]?.complexity ?? 2 };
      const e: Estimate = estimateDroid(ta.value, { cls: o.cls as DroidClass, ...opts });
      out.textContent = '';
      if (!e.ok) { out.appendChild(this.el('p', 'dd-err', `✗ ${e.errors[0] ?? 'does not compile'}`)); return; }
      const grid = this.el('div', 'dd-kv');
      const add = (k: string, val: string, big = false): void => { grid.append(this.el('span', 'k', k), this.el('span', big ? 'dd-big' : 'ax-num', val)); };
      add('Per hour', `${credits(e.perHour)} credits`, true);
      add('Running cost', `${credits(e.runPerHour)} an hour while working`);
      if (e.loops) add('One pass', `${credits(e.passCost)} credits · ${Math.round(e.passSecs)} s (≈ ${credits(3600 / Math.max(1, e.passSecs))} passes an hour)`);
      else add('The whole run', `${credits(e.oneShotCost)} credits · ${Math.round(e.passSecs)} s`);
      if (e.handlerPerHour > 0) add('Handlers', `${credits(e.handlerPerHour)} an hour`);
      if (e.walletPerHour > 0) add('From your wallet (upkeep)', `${credits(e.walletPerHour)} an hour${e.walletCapped ? ` — capped at ${UPKEEP_HOUR_CAP} an hour per droid` : ''}`);
      add(`${o.hours} hours cost`, `${credits(e.loops || e.handlerPerHour > 0 ? costForHours(o.hours, e.perHour) : e.oneShotCost)} credits`);
      add(`${credits(o.pool)} credits last`, e.loops || e.handlerPerHour > 0 ? duration(runtimeHours(o.pool, e.perHour)) : o.pool >= e.oneShotCost ? `the whole run (${credits(o.pool - e.oneShotCost)} left)` : 'not the whole run');
      out.appendChild(grid);
      for (const w of e.warnings) out.appendChild(this.el('p', 'dd-warn', `⚠ ${w}`));
      if (e.assumptions.length) {
        const ul = this.el('ul', 'dd-note');
        for (const a of e.assumptions) ul.appendChild(this.el('li', '', a));
        out.append(this.el('div', 'dd-note', 'Assumptions:'), ul);
      }
      if (e.lines.length) {
        const tb = this.el('table');
        const hr = this.el('tr');
        for (const h of ['Line', 'What', 'Runs', 'Seconds', 'Credits']) hr.appendChild(this.el('th', '', h));
        tb.appendChild(hr);
        for (const l of e.lines.filter((x) => x.cost > 0.0005 || x.secs > 0).slice(0, 40)) {
          const tr = this.el('tr');
          tr.append(this.el('td', 'num', String(l.line)), this.el('td', '', l.what), this.el('td', 'num', credits(l.times)), this.el('td', 'num', credits(l.secs)), this.el('td', 'num', credits(l.cost)));
          tb.appendChild(tr);
        }
        out.append(this.el('div', 'ax-head', 'Where the credits go (one pass, handlers per hour)'), tb);
      }
      // The same program on every class that can run it.
      const cmp = this.el('table');
      const hr2 = this.el('tr');
      for (const h of ['Class', 'Per hour', `${o.hours} h`, 'Pool lasts']) hr2.appendChild(this.el('th', '', h));
      cmp.appendChild(hr2);
      for (const k of DROID_CLASS_ORDER) {
        const ek = estimateDroid(ta.value, { cls: k, ...opts });
        const tr = this.el('tr');
        tr.appendChild(this.el('td', '', DROID_CLASSES[k].name));
        if (!ek.ok) { const td = this.el('td', 'dd-note', 'cannot run it'); td.colSpan = 3; tr.appendChild(td); }
        else tr.append(this.el('td', 'num', credits(ek.perHour)), this.el('td', 'num', credits(costForHours(o.hours, ek.perHour))), this.el('td', 'num', duration(runtimeHours(o.pool, ek.perHour))));
        cmp.appendChild(tr);
      }
      out.append(this.el('div', 'ax-head', 'Every class'), cmp);
    };
    // DEBOUNCED (2026-09-25): every keystroke in a number field ran the estimate seven times (this program, then every
    // class) with no pause. Every field and the text box now share one 250 ms debounce; the draft is kept at once.
    let deb: number | null = null;
    const later = (): void => { if (deb !== null) window.clearTimeout(deb); deb = window.setTimeout(() => { deb = null; run(); }, 250); };
    for (const i of [clsSel, q, hours, pool, lg, ev]) i.addEventListener('input', later);
    ta.oninput = () => { if (d) this.srcDraft.set(key, ta.value); later(); };
    run();
  }

  // ─── Reports ───────────────────────────────────────────────────────────────

  private reports(el: HTMLElement, d: DroidRowLike): void {
    const c = this.conn();
    if (!c?.identity) return;
    const all = [...c.db.droidReport.iter()].filter((r) => r.owner.isEqual(c.identity!)).sort((a, b) => (a.reportId > b.reportId ? -1 : 1));
    const mine = all.filter((r) => r.droidId === d.droidId);
    const list = mine.length ? mine : all;
    el.appendChild(this.el('div', 'ax-head', mine.length ? `Reports from ${d.name}` : 'Reports (all your droids)'));
    if (list.length === 0) { el.appendChild(this.el('p', 'dd-note', 'No reports yet — run a survey (`survey ferrite_ore 200 40`), or `note` and `report` in a program.')); return; }
    const row = this.el('div', 'dd-row');
    for (const r of list.slice(0, 20)) {
      const b = this.btn(`${r.title}`, () => { this.reportSel = r.reportId; this.render(true); }, false, '', `report:${r.reportId}`);
      if (r.reportId === this.reportSel) b.classList.add('is-primary');
      row.appendChild(b);
    }
    el.appendChild(row);
    const r = list.find((x) => x.reportId === this.reportSel) ?? list[0];
    this.reportSel = r.reportId;
    let body: Record<string, unknown> = {};
    try { body = JSON.parse(r.bodyJson) as Record<string, unknown>; } catch { /* raw */ }
    const when = new Date(Number(r.at.microsSinceUnixEpoch / 1000n));
    el.appendChild(this.el('div', 'ax-head', `${r.title} — ${r.droidName}, ${when.toLocaleString()}`));
    if (r.kind === 'survey') {
      const pts = (body.points as SurveyPoint[]) ?? [];
      const best = (body.best as SurveyPoint[]) ?? [];
      el.appendChild(this.el('p', '', `${String(body.label ?? body.family)} · ${pts.length} points · average ${Math.round(Number(body.avg ?? 0) * 100)} % · radius ${String(body.radius)} m, step ${String(body.step)} m`));
      const heat = surveyHeat(pts, Number(body.step ?? 50));
      if (heat.cols > 0) {
        const cv = this.el('canvas', 'dd-map');
        const cell = Math.max(6, Math.floor(240 / Math.max(heat.cols, heat.rows)));
        cv.width = heat.cols * cell; cv.height = heat.rows * cell;
        const g = cv.getContext('2d');
        if (g) {
          const lo = themeColor('--ax-bg3'), hi = themeColor('--ax-good'), none = themeColor('--ax-bg');
          for (let j = 0; j < heat.rows; j++) for (let i = 0; i < heat.cols; i++) {
            const v = heat.cells[j * heat.cols + i];
            g.fillStyle = v < 0 ? none : mixColor(lo, hi, Math.min(1, v / Math.max(0.01, best[0]?.c ?? 1)));
            g.fillRect(i * cell, j * cell, cell - 1, cell - 1);
          }
        }
        el.append(cv, this.el('div', 'dd-note', 'North is up; brighter is richer.'));
      }
      const tb = this.el('table');
      const hr = this.el('tr');
      for (const h of ['Best spots', 'Concentration', 'Spawn']) hr.appendChild(this.el('th', '', h));
      tb.appendChild(hr);
      for (const p of best) {
        const tr = this.el('tr');
        tr.append(this.el('td', 'num', `(${Math.round(p.x)}, ${Math.round(p.z)})`), this.el('td', 'num', `${Math.round(p.c * 100)} %`), this.el('td', '', p.s || '—'));
        tb.appendChild(tr);
      }
      el.appendChild(tb);
    } else if (r.kind === 'notes') {
      const ul = this.el('ul');
      for (const l of (body.lines as string[]) ?? []) ul.appendChild(this.el('li', '', l));
      el.appendChild(ul);
    } else if (r.kind === 'ship') {
      const comps = (body.comps as Array<{ slot: string; name: string; quality: number }>) ?? [];
      const hold = body.hold as { used: number; cap: number } | undefined;
      const fit = body.fit as { cpu: number; pg: number; budget: { cpu: number; pg: number } } | undefined;
      el.appendChild(this.el('p', '', `${String(body.name)} (${String(body.chassis)}) · hold ${hold?.used ?? 0}/${hold?.cap ?? 0} · CPU ${fit?.cpu ?? 0}/${fit?.budget.cpu ?? 0} · PG ${fit?.pg ?? 0}/${fit?.budget.pg ?? 0}`));
      const tb = this.el('table');
      for (const cp of comps) { const tr = this.el('tr'); tr.append(this.el('td', '', cp.slot), this.el('td', '', cp.name), this.el('td', 'num', `q${cp.quality}`)); tb.appendChild(tr); }
      el.appendChild(tb);
    } else {
      el.appendChild(this.el('pre', 'dd-log', JSON.stringify(body, null, 2)));
    }
    el.appendChild(this.btn('Delete this report', () => this.call(() => c.reducers.deleteDroidReport({ reportId: r.reportId }), 'Deleted'), false, '', `report-delete:${r.reportId}`));
  }

  // ─── Log ───────────────────────────────────────────────────────────────────

  /** The droid's log: its owner's `droid_log` row (2026-09-28 — the lines left the public unit row, which every client
   *  received), else a pre-migration `droid_unit.logJson`. */
  private logJsonOf(d: DroidRowLike): string {
    let s: string | undefined;
    try { s = this.conn()?.db.droidLog.droidId.find(d.droidId)?.logJson; } catch { /* cache churn */ }
    return s ?? d.logJson ?? '[]';
  }

  private log(el: HTMLElement, d: DroidRowLike): void {
    let log: Array<{ t: string; msg: string }> = [];
    try { log = JSON.parse(this.logJsonOf(d)) as typeof log; } catch { /* fresh */ }
    if (!Array.isArray(log)) log = [];
    const box = this.el('div', 'dd-log');
    box.textContent = log.slice().reverse().map((l) => {
      // THE FINITE LAW on the way in too (2026-09-28): a malformed stamp threw inside BigInt() and blanked the tab.
      const us = Number(l?.t);
      const t = new Date(Number.isFinite(us) ? us / 1000 : 0);
      return `[${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')}:${t.getSeconds().toString().padStart(2, '0')}] ${l.msg}`;
    }).join('\n') || 'Nothing logged yet.';
    el.appendChild(box);
  }
}
